/**
 * Water Level Hydrograph Chart — TradingView-style interactive chart
 *
 * Built on lightweight-charts (TradingView's open-source engine):
 *   • Hover/scroll on bottom (time) axis → expand/compress X scale
 *   • Hover/scroll on right (price) axis → expand/compress Y scale
 *   • Drag-grab chart body → pan around to investigate other areas
 *   • Mouse wheel on chart → zoom in/out
 *   • Crosshair tooltip with all visible series values at the cursor
 *
 * Aquifer filtering: when `aquiferFilter` is set (and not '__combined__'),
 * only renders wells where well.aquifer_name matches the filter.
 *
 * Recharts version preserved as WaterLevelChart.recharts.tsx for fallback.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  LineSeries,
  LineStyle,
  Time,
  CrosshairMode,
  ColorType,
} from 'lightweight-charts';
import type { WellTimeseries, PredictionStep } from '../../api/phreatic';

// 15 blue shades — same palette as the Recharts version
const WELL_COLORS = [
  '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af',
  '#93c5fd', '#6db3f8', '#4a9af5', '#2d7de0', '#1a5fc0',
  '#bfdbfe', '#7cb9f7', '#5b9eee', '#3a83d6', '#2468b8',
];

interface WellPredictions {
  well_name: string;
  steps: PredictionStep[];
}

interface WaterLevelChartProps {
  wells: WellTimeseries[];
  selectedDate?: string | null;
  onDateSelect?: (date: string) => void;
  predictions?: WellPredictions[];
  showPredictions?: boolean;
  /** Filter to a specific aquifer. null or '__combined__' = show all. */
  aquiferFilter?: string | null;
}

export function WaterLevelChart({
  wells,
  selectedDate,
  onDateSelect,
  predictions,
  showPredictions,
  aquiferFilter,
}: WaterLevelChartProps) {
  const [metric, setMetric] = useState<'depth_to_water_ft' | 'water_elevation_ft'>('water_elevation_ft');
  const [hiddenWells, setHiddenWells] = useState<Set<string>>(new Set());
  const [showWellList, setShowWellList] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const markerSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  // ── Filter wells by aquifer ──
  const filteredWells = useMemo(() => {
    if (!aquiferFilter || aquiferFilter === '__combined__') return wells;
    return wells.filter(w => (w.aquifer_name ?? 'other') === aquiferFilter);
  }, [wells, aquiferFilter]);

  // ── Sorted well names (numeric-aware) ──
  const sortedWellNames = useMemo(() => {
    return [...filteredWells.map(w => w.well_name)].sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.replace(/\D/g, '')) || 0;
      return numA - numB || a.localeCompare(b);
    });
  }, [filteredWells]);

  const visibleCount = sortedWellNames.filter(n => !hiddenWells.has(n)).length;

  // Map well_name → original index for stable colors
  const wellColorIndex = useMemo(() => {
    const map = new Map<string, number>();
    filteredWells.forEach((w, i) => map.set(w.well_name, i));
    return map;
  }, [filteredWells]);

  // ── Init chart once ──
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#888',
        fontSize: 11,
        fontFamily: 'inherit',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#333', style: LineStyle.Dashed },
        horzLines: { color: '#333', style: LineStyle.Dashed },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#555',
        rightOffset: 5,
      },
      rightPriceScale: {
        borderColor: '#555',
        autoScale: true,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      crosshair: { mode: CrosshairMode.Normal },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: { time: true, price: true },
        mouseWheel: true,
        pinch: true,
      },
    });
    chartRef.current = chart;

    // Manual resize handler (v4 doesn't support autoSize prop)
    const resize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
          height: 350,
        });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    const ro = new ResizeObserver(resize);
    if (containerRef.current) ro.observe(containerRef.current);

    // Click → date select
    chart.subscribeClick(param => {
      if (param.time && onDateSelect) {
        // Time may be a string (YYYY-MM-DD) or business day object
        let d: string | null = null;
        if (typeof param.time === 'string') {
          d = param.time;
        } else if (typeof param.time === 'object' && 'year' in param.time) {
          const t = param.time as { year: number; month: number; day: number };
          d = `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
        }
        if (d) onDateSelect(d);
      }
    });

    return () => {
      window.removeEventListener('resize', resize);
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current.clear();
      markerSeriesRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-apply DTW invert when metric changes ──
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.priceScale('right').applyOptions({
      invertScale: metric === 'depth_to_water_ft',
    });
  }, [metric]);

  // ── Update series when data/aquifer/metric/predictions change ──
  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    const series = seriesRef.current;

    // Tear down series for wells no longer present (aquifer filter changed, etc.)
    const wantedNames = new Set<string>();
    filteredWells.forEach(w => {
      wantedNames.add(w.well_name);
      wantedNames.add(`${w.well_name}__pred`);
    });
    Array.from(series.entries()).forEach(([key, s]) => {
      if (!wantedNames.has(key)) {
        try { chart.removeSeries(s); } catch {}
        series.delete(key);
      }
    });

    // Add/update each well's historical line
    filteredWells.forEach((well) => {
      const name = well.well_name;
      const idx = wellColorIndex.get(name) ?? 0;
      // Synthetic single-reading-well clusters render as a SOLID violet chain (the
      // historical gauging series). Their V6 forecast is drawn dashed below — same
      // solid-history / dashed-forecast convention as gauged wells, just in violet.
      const isSynthetic = !!well.well_id?.startsWith('cluster:');
      const color = isSynthetic ? '#a855f7' : WELL_COLORS[idx % WELL_COLORS.length];

      // Deduplicate by calendar day — lightweight-charts requires strictly
      // ascending time and treats YYYY-MM-DD as a business day. Two different
      // input strings (e.g. "1999-02-08" and "1999-02-08T08:00:00Z") collapse to
      // the same epoch, so we MUST normalize to the day prefix before keying.
      const dedupMap = new Map<string, number>();
      for (const m of well.measurements) {
        if (!m.date) continue;
        const v = metric === 'water_elevation_ft' ? m.water_elevation_ft : m.depth_to_water_ft;
        if (v == null || !isFinite(v)) continue;
        const day = m.date.slice(0, 10); // YYYY-MM-DD
        if (day.length !== 10) continue;
        dedupMap.set(day, v); // last write wins
      }
      const data = Array.from(dedupMap.entries())
        .map(([date, value]) => ({ time: date as Time, value }))
        .sort((a, b) => (a.time as string).localeCompare(b.time as string));

      let s = series.get(name);
      if (!s) {
        s = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          lineStyle: LineStyle.Solid,
          priceLineVisible: false,
          lastValueVisible: false,
          title: name,
        });
        series.set(name, s);
      } else {
        s.applyOptions({ color, lineStyle: LineStyle.Solid });
      }

      s.setData(data);
      s.applyOptions({ visible: !hiddenWells.has(name) });
    });

    // Predictions (dashed lines)
    if (showPredictions && predictions) {
      filteredWells.forEach((well) => {
        const name = well.well_name;
        const wp = predictions.find(p => p.well_name === name);
        if (!wp) return;
        const idx = wellColorIndex.get(name) ?? 0;
        // Cluster forecast stays violet (dashed) to match its solid violet chain.
        const isSynthetic = !!well.well_id?.startsWith('cluster:');
        const color = isSynthetic ? '#a855f7' : WELL_COLORS[idx % WELL_COLORS.length];
        const predKey = `${name}__pred`;

        // Dedupe predictions by calendar day (normalize date prefix to YYYY-MM-DD)
        const predDedup = new Map<string, number>();
        for (const step of wp.steps) {
          if (!step.target_date) continue;
          const v = metric === 'water_elevation_ft' ? step.p50 : step.depth_to_water_ft;
          if (v == null || !isFinite(v as number)) continue;
          const day = step.target_date.slice(0, 10);
          if (day.length !== 10) continue;
          predDedup.set(day, v as number);
        }
        const predData = Array.from(predDedup.entries())
          .map(([date, value]) => ({ time: date as Time, value }))
          .sort((a, b) => (a.time as string).localeCompare(b.time as string));

        let ps = series.get(predKey);
        if (!ps) {
          ps = chart.addSeries(LineSeries, {
            color,
            lineWidth: 2,
            lineStyle: LineStyle.Dashed,
            priceLineVisible: false,
            lastValueVisible: false,
            title: `${name} (predicted)`,
          });
          series.set(predKey, ps);
        } else {
          ps.applyOptions({ color });
        }
        ps.setData(predData);
        ps.applyOptions({ visible: !hiddenWells.has(name) });
      });
    } else {
      // Remove prediction series if predictions are off
      Array.from(series.entries()).forEach(([key, s]) => {
        if (key.endsWith('__pred')) {
          try { chart.removeSeries(s); } catch {}
          series.delete(key);
        }
      });
    }

    // ── TODAY marker removed per UX request ──
    // (Clear any leftover marker if it was previously set)
    if (markerSeriesRef.current) {
      try { markerSeriesRef.current.setMarkers([]); } catch {}
      markerSeriesRef.current = null;
    }

    // Auto-fit content on data refresh (only when no zoom state to preserve)
    chart.timeScale().fitContent();
  }, [filteredWells, metric, predictions, showPredictions, hiddenWells, wellColorIndex]);

  // ── Resize on aquifer filter change to keep layout clean ──
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, [aquiferFilter]);

  const toggleWell = (name: string) => {
    setHiddenWells(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const showAll = () => setHiddenWells(new Set());
  const hideAll = () => setHiddenWells(new Set(sortedWellNames));
  // Hide every well that doesn't have V5 predictions (i.e. didn't have enough
  // gauging history for inference). Reference-only wells get hidden so the user
  // sees just the inference-capable cohort.
  const showPredictableOnly = () => {
    const predictableNames = new Set((predictions ?? []).map(p => p.well_name));
    const next = new Set<string>();
    for (const name of sortedWellNames) {
      if (!predictableNames.has(name)) next.add(name);
    }
    setHiddenWells(next);
  };
  const predictableCount = (predictions ?? []).filter(p => sortedWellNames.includes(p.well_name)).length;

  if (filteredWells.length === 0) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-500 text-sm">
          {aquiferFilter && aquiferFilter !== '__combined__'
            ? `No wells in "${aquiferFilter}" aquifer at this site`
            : 'No water level data available'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
          Groundwater Hydrograph
          {aquiferFilter && aquiferFilter !== '__combined__' && (
            <span className="text-cyan-400 ml-2 text-xs normal-case font-normal">
              [{aquiferFilter} only]
            </span>
          )}
        </h3>
        <div className="flex gap-2 items-center">
          <button onClick={() => setShowWellList(!showWellList)}
            className="px-2 py-1 text-[10px] rounded bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300">
            Wells ({visibleCount}/{sortedWellNames.length})
          </button>
          <button onClick={() => setMetric('water_elevation_ft')}
            className={`px-2 py-1 text-[10px] rounded transition-colors ${metric === 'water_elevation_ft' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'bg-mono-800 text-mono-400 border border-mono-700'}`}>
            Elevation
          </button>
          <button onClick={() => setMetric('depth_to_water_ft')}
            className={`px-2 py-1 text-[10px] rounded transition-colors ${metric === 'depth_to_water_ft' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'bg-mono-800 text-mono-400 border border-mono-700'}`}>
            DTW
          </button>
        </div>
      </div>

      {/* Well toggle list */}
      {showWellList && (
        <div className="mb-3 bg-mono-800/50 rounded-lg p-2">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-mono-500 uppercase tracking-wider">Toggle Wells:</span>
            <button onClick={showAll} className="text-[9px] text-cyan-400 hover:text-cyan-300">Show All</button>
            <button onClick={hideAll} className="text-[9px] text-mono-500 hover:text-mono-400">Hide All</button>
            <button
              onClick={showPredictableOnly}
              disabled={predictableCount === 0}
              title={predictableCount === 0
                ? 'No wells in this view have V5 predictions yet'
                : `Show only the ${predictableCount} well${predictableCount === 1 ? '' : 's'} with V5 inference (hide reference-only wells)`}
              className="text-[9px] text-cyan-400 hover:text-cyan-300 disabled:text-mono-600 disabled:cursor-not-allowed"
            >
              Predictable Only ({predictableCount})
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {sortedWellNames.map((name, idx) => {
              const origIdx = wellColorIndex.get(name) ?? 0;
              const color = WELL_COLORS[origIdx % WELL_COLORS.length];
              const hidden = hiddenWells.has(name);
              return (
                <button key={name} onClick={() => toggleWell(name)}
                  className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors ${hidden ? 'bg-mono-800 text-mono-600' : 'text-mono-200'}`}
                  style={{ borderLeft: `3px solid ${hidden ? '#555' : color}` }}>
                  {idx + 1}. {name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div ref={containerRef} style={{ height: 350, width: '100%' }} />

      <div className="mt-2 flex items-center justify-between text-[10px] text-mono-500">
        <div>
          <span className="text-cyan-400">●</span> Drag chart body to pan
          <span className="mx-2">·</span>
          <span className="text-cyan-400">↕</span> Hover/drag right axis to scale Y
          <span className="mx-2">·</span>
          <span className="text-cyan-400">↔</span> Hover/drag bottom axis to scale X
          <span className="mx-2">·</span>
          Mouse wheel on chart to zoom
        </div>
        {selectedDate && (
          <span className="text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded">
            Selected: {new Date(selectedDate + 'T00:00:00').toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}
